import { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, CheckCircle, Plus, Trash2, Building2, Users, CreditCard, Landmark, FileCheck, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import api from '../lib/api';
import { toast } from 'sonner';

const TOTAL_STEPS = 7;

const PartnerOnboarding = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  // Step 1: Account Setup
  const [accountData, setAccountData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    password: '',
  });

  // Step 2: Business Profile
  const [businessData, setBusinessData] = useState({
    industry: '',
    monthlyVolume: '',
    averageDealSize: '',
  });

  // Step 3: Team Structure
  const [teamData, setTeamData] = useState({
    director: { name: '', email: '' },
    manager: { name: '', email: '' },
    salesReps: [{ name: '', email: '' }],
  });

  // Step 4: Billing Contact
  const [billingData, setBillingData] = useState({
    contactName: '',
    email: '',
    phone: '',
  });

  // Step 5: Payout Information
  const [payoutData, setPayoutData] = useState({
    businessName: '',
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: '',
  });

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setIsComplete(false);
      setAccountData({ firstName: '', lastName: '', email: '', phone: '', companyName: '', password: '' });
      setBusinessData({ industry: '', monthlyVolume: '', averageDealSize: '' });
      setTeamData({ director: { name: '', email: '' }, manager: { name: '', email: '' }, salesReps: [{ name: '', email: '' }] });
      setBillingData({ contactName: '', email: '', phone: '' });
      setPayoutData({ businessName: '', bankName: '', accountNumber: '', routingNumber: '', accountType: '' });
    }
  }, [isOpen]);

  // Add sales rep
  const addSalesRep = () => {
    setTeamData(prev => ({
      ...prev,
      salesReps: [...prev.salesReps, { name: '', email: '' }]
    }));
  };

  // Remove sales rep
  const removeSalesRep = (index) => {
    if (teamData.salesReps.length > 1) {
      setTeamData(prev => ({
        ...prev,
        salesReps: prev.salesReps.filter((_, i) => i !== index)
      }));
    }
  };

  // Update sales rep
  const updateSalesRep = (index, field, value) => {
    setTeamData(prev => ({
      ...prev,
      salesReps: prev.salesReps.map((rep, i) => 
        i === index ? { ...rep, [field]: value } : rep
      )
    }));
  };

  // Validate current step
  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return accountData.firstName && accountData.lastName && accountData.email && accountData.phone && accountData.companyName && accountData.password;
      case 2:
        return businessData.industry && businessData.monthlyVolume && businessData.averageDealSize;
      case 3:
        return teamData.director.name && teamData.director.email;
      case 4:
        return billingData.contactName && billingData.email && billingData.phone;
      case 5:
        return payoutData.businessName && payoutData.bankName && payoutData.accountNumber && payoutData.routingNumber && payoutData.accountType;
      case 6:
        return true;
      default:
        return true;
    }
  };

  // Handle next step
  const handleNext = () => {
    if (!validateStep()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle back
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const applicationData = {
        // Account info
        account: accountData,
        // Business profile
        business: businessData,
        // Team structure
        team: {
          director: teamData.director,
          manager: teamData.manager.name ? teamData.manager : null,
          salesReps: teamData.salesReps.filter(rep => rep.name && rep.email),
        },
        // Billing contact
        billing: billingData,
        // Payout info
        payout: payoutData,
        // Meta
        status: 'paused',
        plan: 'Standard Partner',
        createdAt: new Date().toISOString(),
      };

      // Submit to backend
      await api.post('/partner-applications', applicationData);
      
      setIsComplete(true);
      setCurrentStep(7);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Application submission error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  const stepIcons = [
    <Building2 key="1" className="h-4 w-4" />,
    <Users key="2" className="h-4 w-4" />,
    <Users key="3" className="h-4 w-4" />,
    <CreditCard key="4" className="h-4 w-4" />,
    <Landmark key="5" className="h-4 w-4" />,
    <FileCheck key="6" className="h-4 w-4" />,
    <CheckCircle key="7" className="h-4 w-4" />,
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden bg-white max-h-[90vh] overflow-y-auto">
        {/* Progress Bar */}
        <div className="h-1 bg-slate-200">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center gap-2 pt-6 pb-2">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={`step-${i + 1}`}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                i + 1 === currentStep 
                  ? 'bg-primary text-white' 
                  : i + 1 < currentStep 
                    ? 'bg-green-500 text-white' 
                    : 'bg-slate-200 text-slate-400'
              }`}
            >
              {i + 1 < currentStep ? <CheckCircle className="h-4 w-4" /> : stepIcons[i]}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="p-8">
          {/* Step 1: Account Setup */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Activate Your Access</h2>
                <p className="text-slate-600">Create your partner account to get started.</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={accountData.firstName}
                    onChange={(e) => setAccountData({ ...accountData, firstName: e.target.value })}
                    placeholder="John"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={accountData.lastName}
                    onChange={(e) => setAccountData({ ...accountData, lastName: e.target.value })}
                    placeholder="Smith"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={accountData.email}
                  onChange={(e) => setAccountData({ ...accountData, email: e.target.value })}
                  placeholder="john@company.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={accountData.phone}
                  onChange={(e) => setAccountData({ ...accountData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  value={accountData.companyName}
                  onChange={(e) => setAccountData({ ...accountData, companyName: e.target.value })}
                  placeholder="Your Company LLC"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Create Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={accountData.password}
                  onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            </div>
          )}

          {/* Step 2: Business Profile */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Tell Us About Your Operation</h2>
                <p className="text-slate-600">Help us understand your business to serve you better.</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="industry">Industry *</Label>
                <Select
                  value={businessData.industry}
                  onValueChange={(value) => setBusinessData({ ...businessData, industry: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="merchant_services">Merchant Services</SelectItem>
                    <SelectItem value="business_lending">Business Lending</SelectItem>
                    <SelectItem value="equipment_finance">Equipment Finance</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="financial_services">Financial Services</SelectItem>
                    <SelectItem value="consulting">Consulting</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="monthlyVolume">Monthly Deal Volume *</Label>
                <Select
                  value={businessData.monthlyVolume}
                  onValueChange={(value) => setBusinessData({ ...businessData, monthlyVolume: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your monthly volume" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-5">1-5 deals/month</SelectItem>
                    <SelectItem value="6-10">6-10 deals/month</SelectItem>
                    <SelectItem value="11-20">11-20 deals/month</SelectItem>
                    <SelectItem value="21-50">21-50 deals/month</SelectItem>
                    <SelectItem value="50+">50+ deals/month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="averageDealSize">Average Deal Size *</Label>
                <Select
                  value={businessData.averageDealSize}
                  onValueChange={(value) => setBusinessData({ ...businessData, averageDealSize: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select average deal size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-10k">Under $10,000</SelectItem>
                    <SelectItem value="10k-25k">$10,000 - $25,000</SelectItem>
                    <SelectItem value="25k-50k">$25,000 - $50,000</SelectItem>
                    <SelectItem value="50k-100k">$50,000 - $100,000</SelectItem>
                    <SelectItem value="100k-250k">$100,000 - $250,000</SelectItem>
                    <SelectItem value="250k+">$250,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3: Team Structure */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Set Up Your Team</h2>
                <p className="text-slate-600">You can assign roles and control visibility across your organization.</p>
              </div>
              
              {/* Director */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium bg-primary text-white px-2 py-1 rounded">DIRECTOR</span>
                  <span className="text-xs text-slate-500">Full access to all deals and reporting</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={teamData.director.name}
                      onChange={(e) => setTeamData({ ...teamData, director: { ...teamData.director, name: e.target.value } })}
                      placeholder="Director name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={teamData.director.email}
                      onChange={(e) => setTeamData({ ...teamData, director: { ...teamData.director, email: e.target.value } })}
                      placeholder="director@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Manager */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium bg-slate-600 text-white px-2 py-1 rounded">MANAGER</span>
                  <span className="text-xs text-slate-500">Access only to assigned team (optional)</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      value={teamData.manager.name}
                      onChange={(e) => setTeamData({ ...teamData, manager: { ...teamData.manager, name: e.target.value } })}
                      placeholder="Manager name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={teamData.manager.email}
                      onChange={(e) => setTeamData({ ...teamData, manager: { ...teamData.manager, email: e.target.value } })}
                      placeholder="manager@company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Sales Reps */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium bg-slate-400 text-white px-2 py-1 rounded">SALES REPS</span>
                    <span className="text-xs text-slate-500">Access only to their own deals</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSalesRep}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Sales Rep
                  </Button>
                </div>
                <div className="space-y-3">
                  {teamData.salesReps.map((rep, index) => (
                    <div key={rep.email || `rep-${index}`} className="grid grid-cols-2 gap-4 relative">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={rep.name}
                          onChange={(e) => updateSalesRep(index, 'name', e.target.value)}
                          placeholder="Rep name"
                        />
                      </div>
                      <div className="space-y-2 relative">
                        <Label>Email</Label>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            value={rep.email}
                            onChange={(e) => updateSalesRep(index, 'email', e.target.value)}
                            placeholder="rep@company.com"
                          />
                          {teamData.salesReps.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeSalesRep(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Billing Contact */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Billing & Account Responsibility</h2>
                <p className="text-slate-600">This person is responsible for billing, payments, and account management.</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="billingName">Billing Contact Name *</Label>
                <Input
                  id="billingName"
                  value={billingData.contactName}
                  onChange={(e) => setBillingData({ ...billingData, contactName: e.target.value })}
                  placeholder="Jane Smith"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="billingEmail">Billing Email *</Label>
                <Input
                  id="billingEmail"
                  type="email"
                  value={billingData.email}
                  onChange={(e) => setBillingData({ ...billingData, email: e.target.value })}
                  placeholder="billing@company.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="billingPhone">Billing Phone *</Label>
                <Input
                  id="billingPhone"
                  type="tel"
                  value={billingData.phone}
                  onChange={(e) => setBillingData({ ...billingData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          )}

          {/* Step 5: Payout Information */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Where Should We Send Your Funds</h2>
                <p className="text-slate-600">This is where your funded deals will be wired.</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name on Account *</Label>
                <Input
                  id="businessName"
                  value={payoutData.businessName}
                  onChange={(e) => setPayoutData({ ...payoutData, businessName: e.target.value })}
                  placeholder="Your Company LLC"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bankName">Bank Name *</Label>
                <Input
                  id="bankName"
                  value={payoutData.bankName}
                  onChange={(e) => setPayoutData({ ...payoutData, bankName: e.target.value })}
                  placeholder="Chase, Bank of America, etc."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="accountNumber">Account Number *</Label>
                  <Input
                    id="accountNumber"
                    value={payoutData.accountNumber}
                    onChange={(e) => setPayoutData({ ...payoutData, accountNumber: e.target.value })}
                    placeholder="••••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="routingNumber">Routing Number *</Label>
                  <Input
                    id="routingNumber"
                    value={payoutData.routingNumber}
                    onChange={(e) => setPayoutData({ ...payoutData, routingNumber: e.target.value })}
                    placeholder="•••••••••"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="accountType">Account Type *</Label>
                <Select
                  value={payoutData.accountType}
                  onValueChange={(value) => setPayoutData({ ...payoutData, accountType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 6: Application Submission */}
          {currentStep === 6 && (
            <div className="space-y-6 text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <FileCheck className="h-8 w-8 text-primary" />
              </div>
              
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Submit Your Application</h2>
                <p className="text-slate-600">Your information will be reviewed and your account will be prepared for activation.</p>
              </div>
              
              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4 text-left space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Company</span>
                  <span className="font-medium text-slate-900">{accountData.companyName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Contact</span>
                  <span className="font-medium text-slate-900">{accountData.firstName} {accountData.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Industry</span>
                  <span className="font-medium text-slate-900">{businessData.industry.replace(/_/g, ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Monthly Volume</span>
                  <span className="font-medium text-slate-900">{businessData.monthlyVolume} deals</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Plan</span>
                  <span className="font-medium text-primary">Standard Partner</span>
                </div>
              </div>
              
              <p className="text-sm text-slate-500">
                By submitting, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          )}

          {/* Step 7: Confirmation */}
          {currentStep === 7 && (
            <div className="space-y-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              
              <div>
                <h2 className="font-heading text-2xl text-slate-900 mb-2">Your Account Is Being Prepared</h2>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-6 text-left space-y-4">
                <p className="text-slate-700">
                  Your application has been received and your account is currently being set up.
                </p>
                <p className="text-slate-600">
                  Our team is preparing your billing profile and system access. This includes setting up your account for processing and ensuring everything is ready for you to begin submitting deals.
                </p>
                <p className="text-slate-700 font-medium">
                  Once setup is complete, your account will be activated and a member of our team will contact you directly to walk you through the next steps.
                </p>
              </div>
              
              <p className="text-amber-600 font-medium">
                This process typically takes 24–48 hours.
              </p>
              
              <div className="flex gap-4 justify-center pt-4">
                <Button variant="outline" onClick={onClose}>
                  Return Home
                </Button>
                <Button variant="ghost" asChild>
                  <a href="mailto:support@prestigiouscompanies.org">Contact Support</a>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 7 && (
          <div className="flex justify-between items-center p-6 border-t bg-slate-50">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 1}
              className={currentStep === 1 ? 'invisible' : ''}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            
            {currentStep === 6 ? (
              <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-primary hover:bg-slate-800">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleNext} className="bg-primary hover:bg-slate-800">
                Continue
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PartnerOnboarding;
